'use client';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  Input,
  InputProps,
  keyframes,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';
import { COLORS, MOTION, RADIUS, VISUALLY_HIDDEN } from '../../constants';

const RESEND_TIMEOUT = 45;

// Blinking caret in the active empty box, as in customer-mobile (on/off every 500ms)
const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

/**
 * Renders Otp inputs with helperText and resend password button.
 *
 * @param {string} fields - The number of inputs to render.
 * @param {Function} resend - The function for the resend button.
 * @param {string} ariaLabel - Accessible name for the code input.
 * @param {boolean} loading - Announces to assistive technologies that the code is being verified.
 * @return {React.JSX.Element} The rendered custom checkbox component.
 */
export const Otp = ({
  fields,
  resend,
  helperText,
  ariaLabel = 'Código de verificación',
  loading = false,
  ...rest
}: {
  fields: number;
  resend: () => void;
  helperText?: string;
  ariaLabel?: string;
  loading?: boolean;
} & InputProps): React.JSX.Element => {
  const { onChange, ...textFieldProps } = rest;
  const value = textFieldProps.value as string;
  const otpRef = useRef<HTMLInputElement[] | null[]>([null]);
  const submitRef = useRef<HTMLButtonElement>(null);
  const [rawValue, setRawValue] = useState(value || '');
  const [focus, setFocus] = useState(false);
  const [smsTimeout, setSmsTimeout] = useState(0);
  const hiddenField = useRef<HTMLInputElement | null>(null);
  const smsInterval = useRef(0);
  const wasCountingDown = useRef(false);
  const [resendStatus, setResendStatus] = useState('');
  const helperId = useId();
  const showHelper = Boolean(textFieldProps.error && helperText);

  useEffect(() => {
    onChange?.({ target: { value: rawValue } } as React.ChangeEvent<HTMLInputElement>);
    if (submitRef?.current && rawValue.length === fields) {
      setTimeout(() => {
        submitRef.current?.click();
      }, 100);
    }
  }, [rawValue]);

  useEffect(() => {
    smsInterval.current = window.setTimeout(() => {
      setSmsTimeout((t) => t - 1);
    }, 1000);
    if (smsTimeout <= 0) clearTimeout(smsInterval.current);
    // Announce only state changes (not every tick) to assistive technologies
    if (smsTimeout > 0) {
      wasCountingDown.current = true;
    } else if (wasCountingDown.current) {
      wasCountingDown.current = false;
      setResendStatus('Ya podés solicitar un nuevo código');
    }
  }, [smsTimeout]);

  useEffect(() => {
    if (textFieldProps?.error && rawValue.length === fields) setRawValue('');
  }, [helperText]);

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <button type="submit" ref={submitRef} style={{ display: 'none' }} />
      <Input
        {...textFieldProps}
        sx={{ height: 0, width: 0, opacity: 0 }}
        ref={hiddenField}
        value={rawValue}
        autoComplete="one-time-code"
        inputProps={{
          maxLength: fields,
          inputMode: 'numeric',
          'data-testid': 'hidden-field',
          'aria-label': ariaLabel,
          'aria-invalid': textFieldProps.error ? true : undefined,
          'aria-describedby': showHelper ? helperId : undefined,
        }}
        onKeyDown={(e) => {
          if (
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Home' ||
            e.key === 'End'
          ) {
            e.preventDefault();
          }
          hiddenField?.current?.getElementsByTagName('input')[0]?.setSelectionRange(fields, fields);
        }}
        onChange={(e) => {
          onChange?.(e);
          setRawValue(e.target.value?.replaceAll(/\D+/g, ''));
          hiddenField?.current?.getElementsByTagName('input')[0]?.setSelectionRange(fields, fields);
        }}
        required={textFieldProps.required}
        autoFocus
        onFocus={() => {
          setFocus(true);
          hiddenField?.current?.getElementsByTagName('input')[0]?.setSelectionRange(fields, fields);
        }}
        onBlur={(e) => {
          textFieldProps.onBlur?.(e);
          setFocus(false);
        }}
      />
      <Grid
        aria-hidden="true"
        onClick={() => {
          hiddenField.current?.click();
        }}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        {[...Array(fields)].map((_, index) => (
          <TextField
            data-focus={focus && (index === rawValue.length || (index === fields - 1 && index === rawValue.length - 1))}
            key={index}
            slotProps={{
              htmlInput: {
                tabIndex: -1,
                inherit: 'true',
                inputMode: 'numeric',
                maxLength: 1,
                style: { fontSize: 32, lineHeight: '40px', textAlign: 'center', padding: 0 },
              },
              input: {
                sx: {
                  pointerEvents: 'none',
                  width: { xs: '48px', sm: '64px' },
                  height: { xs: '56px', sm: '64px' },
                  borderRadius: `${RADIUS.lg}px !important`,
                  '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.5px', borderColor: COLORS.border.default },
                },
              },
            }}
            data-testid={`otp-${index + 1}`}
            data-empty={!rawValue?.[index]}
            inputRef={(el) => (otpRef.current[index] = el)}
            sx={{
              width: 'min-content',
              '& p': { display: 'none' },
              opacity: loading ? 0.4 : 1,
              transition: `opacity ${MOTION.short}ms`,
              '&[data-focus="true"] .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1.5px',
                borderColor: COLORS.brand.main,
              },
              '& .Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: `${COLORS.state.error} !important` },
              '&[data-focus="true"][data-empty="true"] .MuiInputBase-root::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '1.5px',
                height: '60%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: COLORS.brand.main,
                animation: `${caretBlink} 1s step-end infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              },
            }}
            // onBlur={() => {}}
            error={textFieldProps.error}
            value={rawValue?.[index] || ''}
          />
        ))}
        {loading && (
          <CircularProgress
            size={28}
            sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-14px', ml: '-14px' }}
          />
        )}
      </Grid>
      <FormHelperText id={helperId} sx={{ alignSelf: 'center', py: 1 }} error>
        {showHelper && helperText}
      </FormHelperText>
      <Button
        disabled={smsTimeout > 0}
        variant="text"
        onClick={(e) => {
          e.preventDefault();
          setRawValue('');
          otpRef.current?.[0]?.click();
          setSmsTimeout(RESEND_TIMEOUT);
          setResendStatus(`Código enviado. Esperá ${RESEND_TIMEOUT} segundos para solicitar uno nuevo`);
          resend?.();
        }}
        sx={{ width: 'fit-content', alignSelf: 'center' }}
      >
        Reenviar código
      </Button>
      <FormHelperText sx={{ alignSelf: 'center', py: 1, display: smsTimeout > 0 ? 'block' : 'none' }}>
        Código enviado. Esperá 00:{`${smsTimeout}`.padStart(2, '0')} para solicitar uno nuevo
      </FormHelperText>
      <Box component="span" aria-live="polite" sx={VISUALLY_HIDDEN}>
        {resendStatus}
      </Box>
      <Box component="span" role="status" sx={VISUALLY_HIDDEN}>
        {loading ? 'Verificando código…' : ''}
      </Box>
    </Stack>
  );
};
